import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { Form, InputNumber, Table } from "antd";
import { isNullOrBlank, strcmp } from "../../../../swzutils";
import UnitOfMeasureType from "../../enums/UnitOfMeasureType";
import { WholeNumberFormat } from "../../components/FormattersTS";
import { formatDimensions } from "../../../../util/FormatUtil";
import { isStringNotBlank } from "../../../../swzutils/TypeUtils";
const resolveActualUnitOfMeasureType = (freightItem) => {
    let uom = freightItem.unitOfMeasure;
    if (freightItem.netUnitOfMeasure && freightItem.netUnitOfMeasure.trim().length > 0) {
        uom = freightItem.netUnitOfMeasure;
    }
    return UnitOfMeasureType.valueOf(uom);
};
const ConsignmentFreightTable = ({ freight, showDescription = false, netQtyEditable = false, showGrossTare = true, tableSize = "middle", hasActions = false, renderActions }) => {
    const freightEnhanced = freight?.map(f => {
        return {
            freight: f,
            effectiveUOM: resolveActualUnitOfMeasureType(f)
        };
    }) ?? [];
    const hasNetUOMSpecifiedDifferentFromOrdered = freightEnhanced.findIndex(f => {
        const orderedUOM = UnitOfMeasureType.valueOf(f.freight.unitOfMeasure);
        return orderedUOM && f.effectiveUOM && f.effectiveUOM !== orderedUOM;
    }) >= 0;
    const hasNetSpecified = freightEnhanced.findIndex(it => {
        return !!it.freight.netQuantity;
    }) >= 0;
    const hasDimensionWeightFreight = freightEnhanced.findIndex(it => {
        return it.effectiveUOM?.hasDimensions() ?? false;
    }) >= 0;
    const hasGrossTareFreight = showGrossTare && freightEnhanced.findIndex(it => {
        return (it.effectiveUOM?.hasGrossTare() ?? false) || it.freight.cubicToTonneConversionRate;
    }) >= 0;
    const hasNetUOMFreight = freightEnhanced.findIndex(it => {
        return (it.effectiveUOM?.hasNet() ?? false) || it.freight.cubicToTonneConversionRate;
    }) >= 0;
    const hasCubicToTonneConversionRate = freightEnhanced.findIndex(it => {
        return it.freight.cubicToTonneConversionRate && it.freight.cubicToTonneConversionRate > 0;
    }) >= 0;
    const hasWasteRecordIds = freightEnhanced.findIndex(it => {
        return !isNullOrBlank(it.freight.wasteRecordId);
    }) >= 0;
    const columns = [];
    if (hasWasteRecordIds) {
        columns.push({
            title: 'EPA Waste Record Id',
            dataIndex: 'wasteRecordId',
            key: 'wasteRecordId',
        });
    }
    columns.push({
        title: 'Product',
        dataIndex: 'productDescription',
        key: 'productDescription',
        sorter: (a, b) => {
            if (isStringNotBlank(a.productDescription) && isStringNotBlank(b.productDescription)) {
                return strcmp(a.productDescription, b.productDescription);
            }
            return strcmp(a.description, b.description);
        },
        render: (text, record) => {
            if (isStringNotBlank(text)) {
                return text;
            }
            return record.description;
        }
    });
    if (showDescription) {
        columns.push({
            title: 'Additional Details',
            dataIndex: 'description',
            key: 'description',
            sorter: (a, b) => {
                return strcmp(a.description, b.description);
            },
        });
    }
    columns.push((hasNetUOMSpecifiedDifferentFromOrdered)
        ? {
            title: 'Unit of Measure',
            className: 'right',
            children: [{
                    title: 'Ordered',
                    className: 'right',
                    render: (text, record) => (_jsx("span", { children: UnitOfMeasureType.enumDescription(record.unitOfMeasure) }))
                }, {
                    title: 'Net',
                    className: 'right',
                    render: (text, record) => (_jsx("span", { children: UnitOfMeasureType.enumDescription(record.netUnitOfMeasure) }))
                }]
        }
        : {
            title: 'Unit of Measure',
            className: 'right',
            render: (text, record) => {
                const uomType = resolveActualUnitOfMeasureType(record);
                return (_jsx("span", { children: uomType ? uomType.description : "-" }));
            }
        });
    columns.push({
        title: 'Ord. Qty',
        className: 'right',
        sorter: (a, b) => {
            let left = a.orderedQuantity || 0;
            let right = b.orderedQuantity || 0;
            return left - right;
        },
        render: (text, record) => {
            if (netQtyEditable) {
                return (_jsx(Form.Item, { name: ['qtyOverrides', record.id], style: { marginBottom: '0px' }, rules: [{
                            required: true,
                            message: 'Quantity required'
                        }], initialValue: record.orderedQuantity, children: _jsx(InputNumber, { min: 0, step: 0.5, className: "text-right" }) }));
            }
            else {
                return (_jsx(WholeNumberFormat, { value: record.orderedQuantity || 0 }));
            }
        }
    });
    if (hasDimensionWeightFreight) {
        columns.push({
            title: 'Dimensions',
            render: (text, record) => {
                return (_jsx("span", { children: formatDimensions(record) }));
            },
        });
    }
    if (hasDimensionWeightFreight) {
        columns.push({
            title: 'Weight',
            render: (text, record) => {
                if (record.weight) {
                    const totalWeight = Math.round(record.orderedQuantity * record.weight * 100) / 100;
                    const weightUnit = record.weightUnit ? record.weightUnit.toLowerCase() : "";
                    return (_jsxs("span", { children: [record.weight, " ", weightUnit, "\u00A0", record.orderedQuantity > 1 && "(" + totalWeight + " " + weightUnit + ")"] }));
                }
                return null;
            },
        });
    }
    if (hasCubicToTonneConversionRate) {
        columns.push({
            title: 'm3 -> tn',
            dataIndex: 'cubicToTonneConversionRate',
            render: (text, record) => {
                if (record.cubicToTonneConversionRate) {
                    return `${record.cubicToTonneConvertedQty}tn (${record.cubicToTonneConversionRate})`;
                }
                return "";
            },
        });
    }
    if (hasGrossTareFreight) {
        columns.push({
            title: 'Gross',
            dataIndex: 'grossQuantity',
            render: (text /*, record*/) => (_jsx(WholeNumberFormat, { value: text })),
        });
    }
    if (hasGrossTareFreight) {
        columns.push({
            title: 'Tare',
            dataIndex: 'tareQuantity',
            render: (text /*, record*/) => (_jsx(WholeNumberFormat, { value: text })),
        });
    }
    if (hasNetUOMFreight || hasNetSpecified) {
        columns.push({
            title: 'Net',
            dataIndex: 'netQuantity',
            render: (text /*, record*/) => (_jsx(WholeNumberFormat, { value: text })),
        });
    }
    if (hasActions && renderActions && renderActions.length > 0) {
        columns.push({
            title: 'Action',
            render: (text, record, index) => {
                return (hasActions && renderActions)
                    ? renderActions(text, record, index)
                    : null;
            },
        });
    }
    return (_jsx(Table, { columns: columns, dataSource: freight, rowKey: record => record.id, pagination: false, scroll: { x: 600 }, size: tableSize }));
};
export default ConsignmentFreightTable;
